import React, { useEffect, useState, useMemo } from 'react';
import { Select, Table } from 'antd';
import { FormField } from 'components';
import { debounce, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { isBeneficiary } from 'utils';
import { supplierUpdateVoucher } from 'store/forms/actions';

const SupplierEquipmentCosts = ({
  disableVoucherTabs,
  setDisableAction,
  setSimulation,
}) => {
  const dispatch = useDispatch();
  const legalVat = useSelector(state => state.forms.carrier.legalVat);
  const voucherInfo = useSelector(state => state.forms?.supplier?.voucherInfo);
  const ean = useSelector(state => state.forms?.supplier?.ean);
  const error = useSelector(state => state.forms?.supplier?.error);
  const compositeEan = useSelector(
    state => state.forms?.supplier?.compositeEan
  );

  const equipmentType = voucherInfo?.voucher?.equipmentType;
  const isThermosifonas = equipmentType === 'equipmentTypeThermosifonas';

  const debouncedUpdateVoucher = useMemo(
    () =>
      debounce((payload, dispatch) => {
        setSimulation(true);
        dispatch(supplierUpdateVoucher({ simulation: true, params: payload }));
      }, 1000),
    []
  );

  const initialData = voucherInfo?.voucher || {
    base: { amount: '-', vat: '-', final: '-' },
    additional: { amount: '-', vat: '-', final: '-' },
    nonSubsidized: { amount: '-', vat: '-', final: '-' },
    voucher: { amount: '-', vat: '-', final: '-' },
    beneficiary: { amount: '-', vat: '-', final: '-' },
  };

  const [data, setData] = useState(initialData);

  const parseFormattedNumber = val => {
    if (typeof val === 'number') return val;
    if (!val) return 0;

    const normalized = val
      .toString()
      .replace(/\./g, '') // remove thousands separators
      .replace(',', '.'); // convert comma decimal to dot

    return Number(normalized);
  };

  const formatToCurrencyString = value => {
    if (value === null || value === undefined || value === '-' || value === '')
      return '-';

    const numberValue =
      typeof value === 'string' ? parseFormattedNumber(value) : Number(value);

    if (isNaN(numberValue)) return '-';

    return numberValue.toFixed(2).replace('.', ',');
  };

  const handleChange = (type, key, value) => {
    setData(prevData => {
      let updated = {
        ...prevData,
        [type]: {
          ...prevData[type],
          [key]: value,
        },
      };
      if (
        key === 'vat' &&
        ['base', 'additional', 'nonSubsidized'].includes(type)
      ) {
        updated = {
          ...updated,
          base: { ...updated.base, vat: value },
          additional: { ...updated.additional, vat: value },
          nonSubsidized: { ...updated.nonSubsidized, vat: value },
        };
      }
      const safeNumber = val => {
        if (val === null || val === undefined || val === '-' || val === '')
          return 0;
        return parseFormattedNumber(val);
      };
      const basePrice = safeNumber(
        type === 'base' && key === 'amount' ? value : updated.base.amount
      );
      const baseVat = safeNumber(
        type === 'base' && key === 'vat' ? value : updated.base.vat
      );
      const additionalPrice = safeNumber(
        type === 'additional' && key === 'amount'
          ? value
          : updated.additional.amount
      );
      const additionalVatRaw = safeNumber(
        type === 'additional' && key === 'vat' ? value : updated.additional.vat
      );
      const nonSubsidizedPrice = safeNumber(
        type === 'nonSubsidized' && key === 'amount'
          ? value
          : updated.nonSubsidized.amount
      );
      const nonSubsidizedVatRaw = safeNumber(
        type === 'nonSubsidized' && key === 'vat'
          ? value
          : updated.nonSubsidized.vat
      );

      const payload = {
        // otp: '176951',
        voucherCode: voucherInfo?.voucher?.code || '',
        vatId: legalVat,
        ean: ean || '',
        compositeEan: compositeEan || '',
        basePrice,
        baseVat,
        additionalCosts: additionalPrice,
        additionalCostsVat: additionalPrice === 0 ? 0 : additionalVatRaw,
        nonSubsidizedEquipmentCost: nonSubsidizedPrice,
        nonSubsidizedEquipmentCostVat:
          nonSubsidizedPrice === 0 ? 0 : nonSubsidizedVatRaw,
        action: 'RESERVE',
      };

      debouncedUpdateVoucher(payload, dispatch);
      return updated;
    });
  };
  useEffect(() => {
    const safeFormat = val => formatToCurrencyString(val);

    const voucher = voucherInfo?.voucher || {};

    // Base row
    const baseAmount = safeFormat(voucher.basePrice);
    const baseVat = safeFormat(voucher.baseVat);
    const baseFinal = safeFormat(voucher.baseTotal);
    const additionalAmountRaw = voucher.additionalCosts;
    const nonSubsidizedAmountRaw = voucher.nonSubsidizedEquipmentCost;

    setData({
      base: {
        amount: baseAmount,
        vat: baseVat,
        final: baseFinal,
      },
      additional: {
        amount: safeFormat(additionalAmountRaw),
        vat:
          additionalAmountRaw && additionalAmountRaw !== 0
            ? safeFormat(voucher.additionalCostsVat)
            : baseVat,
        final: safeFormat(voucher.additionalCostsTotal),
      },
      nonSubsidized: {
        amount: safeFormat(nonSubsidizedAmountRaw),
        vat:
          nonSubsidizedAmountRaw && nonSubsidizedAmountRaw !== 0
            ? safeFormat(voucher.nonSubsidizedEquipmentCostVat)
            : baseVat,
        final: safeFormat(voucher.nonSubsidizedEquipmentCostTotal),
      },
      voucher: {
        amount: safeFormat(voucher.voucherValue),
        vat: safeFormat(voucher.voucherValueVat),
        final: safeFormat(voucher.voucherValueTotal),
      },
      beneficiary: {
        amount: safeFormat(voucher.beneficiaryAmount),
        vat: safeFormat(voucher.beneficiaryAmountVat),
        final: safeFormat(voucher.beneficiaryAmountTotal),
      },
    });
  }, [voucherInfo?.voucher]);

  useEffect(() => {
    const isMissing = val =>
      val === undefined ||
      val === null ||
      val === '' ||
      val === '-' ||
      Number(val.replace(',', '.')) === 0;
    const { base, additional, nonSubsidized } = data || {};

    let hasError = false;

    // Rule 1: base amount & vat must always be present
    if (isMissing(base?.amount) || isMissing(base?.vat)) {
      hasError = true;
    }

    // Rule 2: If one of additional fields is filled, both must be present
    const additionalFilled = !isMissing(additional?.amount);
    if (
      additionalFilled &&
      (isMissing(additional?.amount) || isMissing(additional?.vat))
    ) {
      hasError = true;
    }

    // Rule 3: Same for nonSubsidized fields
    const nonSubsidizedFilled = !isMissing(nonSubsidized?.amount);
    if (
      nonSubsidizedFilled &&
      (isMissing(nonSubsidized?.amount) || isMissing(nonSubsidized?.vat))
    ) {
      hasError = true;
    }

    // Rule 4: Also disable if ean is missing
    if (isMissing(ean) || error) {
      hasError = true;
    }

    setDisableAction(hasError);
  }, [ean, data, error]);

  const rows = [
    {
      key: 'base',
      label: 'Κόστος Βασικού Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean),
      ...data.base,
    },
    {
      key: 'additional',
      label: 'Κόστος Συμπληρωματικού Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean) && !isThermosifonas,
      ...data.additional,
    },
    {
      key: 'nonSubsidized',
      label: 'Κόστος Μη Επιδοτούμενου Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean),
      ...data.nonSubsidized,
    },
    {
      key: 'voucher',
      label: 'Επιδότηση',
      editable: false,
      ...data.voucher,
    },
    {
      key: 'beneficiary',
      label: 'Πληρωμή Ωφελούμενου',
      editable: false,
      ...data.beneficiary,
    },
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      width: '40%',
    },
    {
      title: 'Ποσό',
      dataIndex: 'amount',
      key: 'amount',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          return (
            <FormField
              type="formattedNumber"
              disabled={!record.editable}
              value={value}
              onChange={val => handleChange(record.key, 'amount', val)}
            />
          );
        }
      },
    },
    {
      title: 'Φ.Π.Α. %',
      dataIndex: 'vat',
      key: 'vat',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          const selectValue =
            value === '-' ||
            value === null ||
            value === undefined ||
            value === ''
              ? undefined
              : String(parseFormattedNumber(value));
          return (
            <Select
              className="vat-select"
              disabled={!record.editable || record.key !== 'base'}
              value={selectValue}
              onChange={val => handleChange(record.key, 'vat', val)}
              showSearch={false}
              options={[
                { value: '17', label: '17%' },
                { value: '24', label: '24%' },
              ]}
            />
          );
        }
      },
    },
    {
      title: 'Τελικό Ποσό',
      dataIndex: 'final',
      key: 'final',
      width: '20%',
      render: value => (
        <span className="bold font-size-16">
          {value} {value !== '-' ? '€' : ''}
        </span>
      ),
      width: '50%',
    },
  ];

  return (
    <div className="voucher-costs-table">
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        bordered={false}
        rowClassName={() => 'custom-row'}
      />
    </div>
  );
};

export default SupplierEquipmentCosts;
